import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authFetch } from '../lib/api-fetch';

/** Plays a call's recording. ElevenLabs exposes no shareable URL, so the audio
 *  is streamed from our bearer-authed `/api/voice/recording` proxy and played as
 *  an object URL (an `<audio src>` can't carry the Authorization header itself).
 *  Renders nothing until the audio loads — a call whose recording isn't ready
 *  (404) simply shows no player. */
export function CallRecording({ conversationId }: { conversationId: string | null }) {
  const { t } = useTranslation();
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    setUrl(null);
    if (!conversationId) return;
    let objectUrl: string | null = null;
    let cancelled = false;
    authFetch(`/api/voice/recording/${encodeURIComponent(conversationId)}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(String(res.status));
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        /* no recording (yet) — leave the player hidden */
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [conversationId]);

  if (!url) return null;
  return (
    <section className="space-y-1">
      <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        <span>{t('calls.recording')}</span>
      </div>
      <audio controls preload="none" src={url} className="w-full">
        <a href={url} target="_blank" rel="noreferrer">
          {t('calls.openRecording')}
        </a>
      </audio>
    </section>
  );
}
